/**
 * Subscription Overrides
 *
 * Overrides Appstle subscription widget labels after render.
 * Uses MutationObserver to detect when Appstle injects its widget and
 * replaces label text while preserving widget structure and pricing.
 *
 * Handles:
 * - Initial page load
 * - Variant changes
 * - Dynamic section re-renders
 * - Bundle switcher tab changes
 */

(function () {
  'use strict';

  const APPSTLE_SELECTORS = {
    // Common Appstle widget container selectors (inspect your live site to confirm)
    widgetRoot: [
      '[data-appstle-subscription-widget]',
      '.appstle_subscription_wrapper',
      '.appstle-subscription-widget',
      '#appstle_subscription_widget',
      '[class*="appstle"][class*="subscription"]',
      '[id*="appstle"][id*="subscription"]',
    ],
    // Selectors for individual purchase option rows
    optionRow: [
      '.appstle_subscription_type_row',
      '.appstle-subscription-option',
      '[class*="appstle"][class*="option"]',
      'label[for*="appstle"]',
    ],
  };

  // Label text patterns to match (case-insensitive)
  const LABEL_PATTERNS = {
    oneTime: [
      'one time purchase',
      'one-time purchase',
      'onetime purchase',
      'one time',
      'buy once',
      'purchase once',
    ],
    subscription: [
      'subscribe and save',
      'subscribe & save',
      'subscribe and never',
      'subscribe & never',
      'never run out',
      'subscription',
      'subscribe',
    ],
  };

  /**
   * Get configuration from the hidden config element
   */
  function getConfig() {
    const configEl = document.querySelector('[data-subscription-overrides-config]');
    if (!configEl) return null;

    const enabled = configEl.dataset.enabled === 'true';
    if (!enabled) return null;

    return {
      oneTimeLabel: configEl.dataset.oneTimeLabel || '',
      subscriptionLabel: configEl.dataset.subscriptionLabel || '',
    };
  }

  /**
   * Find Appstle widget root element(s)
   */
  function findAppstleWidgets() {
    const widgets = [];
    for (const selector of APPSTLE_SELECTORS.widgetRoot) {
      try {
        const elements = document.querySelectorAll(selector);
        elements.forEach((el) => {
          if (!widgets.includes(el)) {
            widgets.push(el);
          }
        });
      } catch (e) {
        // Invalid selector, skip
      }
    }
    return widgets;
  }

  /**
   * Check if text contains a pattern (case-insensitive)
   * Used to identify which row is one-time vs subscription
   */
  function matchesPattern(text, patterns) {
    const normalizedText = text.toLowerCase().trim();
    return patterns.some((pattern) => normalizedText.includes(pattern.toLowerCase()));
  }

  /**
   * Check if text starts with a pattern (case-insensitive)
   * This ensures we match "Subscribe and save" but not "Monthly subscription"
   */
  function startsWithPattern(text, patterns) {
    const normalizedText = text.toLowerCase().trim();
    return patterns.some((pattern) => normalizedText.startsWith(pattern.toLowerCase()));
  }

  /**
   * Replace text content in a node while preserving child elements
   * Only replaces direct text nodes, not nested element text
   */
  function replaceTextContent(element, newText) {
    // Find text nodes that contain the label text
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null, false);

    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) {
      const trimmedText = node.textContent.trim();
      if (trimmedText.length > 0) {
        textNodes.push(node);
      }
    }

    // Replace the first substantial text node (likely the label)
    // Skip very short text (like prices) and text that looks like prices
    for (const textNode of textNodes) {
      const text = textNode.textContent.trim();
      // Skip if it looks like a price or very short
      if (text.length < 4 || /^[\$\€\£]?\d/.test(text)) {
        continue;
      }
      // Check if text STARTS WITH one of our patterns
      // This matches "Subscribe and save" but not "Monthly subscription"
      if (
        startsWithPattern(text, LABEL_PATTERNS.oneTime) ||
        startsWithPattern(text, LABEL_PATTERNS.subscription)
      ) {
        textNode.textContent = newText;
        return true;
      }
    }

    return false;
  }

  /**
   * Override labels in a single widget
   */
  function overrideLabelsInWidget(widget, config) {
    if (!config.oneTimeLabel && !config.subscriptionLabel) return;

    // Track if we've made changes to avoid redundant work
    let changesApplied = false;

    // Strategy 1: Find labeled elements within the widget
    for (const selector of APPSTLE_SELECTORS.optionRow) {
      try {
        const rows = widget.querySelectorAll(selector);
        rows.forEach((row) => {
          // Get all text content to determine which option this is
          const fullText = row.textContent.toLowerCase();

          if (config.oneTimeLabel && matchesPattern(fullText, LABEL_PATTERNS.oneTime)) {
            if (replaceTextContent(row, config.oneTimeLabel)) {
              changesApplied = true;
            }
          } else if (
            config.subscriptionLabel &&
            matchesPattern(fullText, LABEL_PATTERNS.subscription)
          ) {
            if (replaceTextContent(row, config.subscriptionLabel)) {
              changesApplied = true;
            }
          }
        });
      } catch (e) {
        // Invalid selector, skip
      }
    }

    // Strategy 2: Broader search if strategy 1 didn't find anything
    if (!changesApplied) {
      // Look for any labels or spans that might contain the text
      const allTextElements = widget.querySelectorAll('label, span, div, p');
      allTextElements.forEach((el) => {
        const text = el.textContent.toLowerCase().trim();

        // Only process if it directly contains the pattern (not in children)
        const childText = Array.from(el.children)
          .map((c) => c.textContent)
          .join(' ')
          .toLowerCase();
        const directText = text.replace(childText, '').trim();

        if (config.oneTimeLabel && matchesPattern(directText, LABEL_PATTERNS.oneTime)) {
          replaceTextContent(el, config.oneTimeLabel);
        } else if (
          config.subscriptionLabel &&
          matchesPattern(directText, LABEL_PATTERNS.subscription)
        ) {
          replaceTextContent(el, config.subscriptionLabel);
        }
      });
    }
  }

  /**
   * Main function to find and override all Appstle widgets
   */
  function overrideAllWidgets() {
    const config = getConfig();
    if (!config) return;

    const widgets = findAppstleWidgets();
    widgets.forEach((widget) => {
      // Skip if already processed recently (debounce)
      if (widget.dataset.subsOverrideProcessed === 'true') {
        return;
      }

      overrideLabelsInWidget(widget, config);

      // Mark as processed (will be cleared on mutation)
      widget.dataset.subsOverrideProcessed = 'true';
    });
  }

  /**
   * Set up MutationObserver to watch for Appstle widget injection
   */
  function setupObserver() {
    const config = getConfig();
    if (!config) return;

    // Debounce the override function
    let debounceTimer = null;
    const debouncedOverride = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        // Clear processed flags before re-processing
        document.querySelectorAll('[data-subs-override-processed]').forEach((el) => {
          delete el.dataset.subsOverrideProcessed;
        });
        overrideAllWidgets();
      }, 100);
    };

    // Observer for DOM changes (Appstle widget injection)
    const observer = new MutationObserver((mutations) => {
      let shouldProcess = false;

      for (const mutation of mutations) {
        // Check if Appstle-related nodes were added
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if this node or its children match Appstle selectors
              const isAppstle =
                node.className?.includes?.('appstle') ||
                node.id?.includes?.('appstle') ||
                node.querySelector?.('[class*="appstle"], [id*="appstle"]');

              if (isAppstle) {
                shouldProcess = true;
                break;
              }
            }
          }
        }

        // Also watch for text changes within existing widgets
        if (mutation.type === 'characterData') {
          const parent = mutation.target.parentElement;
          if (
            parent?.closest?.(
              '[class*="appstle"], [id*="appstle"], [data-appstle-subscription-widget]'
            )
          ) {
            shouldProcess = true;
          }
        }

        if (shouldProcess) break;
      }

      if (shouldProcess) {
        debouncedOverride();
      }
    });

    // Observe the entire document for Appstle injection
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Initial run after a short delay (Appstle may load async)
    // Multiple retries to catch late-loading widgets
    setTimeout(overrideAllWidgets, 500);
    setTimeout(overrideAllWidgets, 1500);
    setTimeout(overrideAllWidgets, 3000);
    setTimeout(overrideAllWidgets, 5000);
    setTimeout(overrideAllWidgets, 8000);
  }

  /**
   * Listen for Shopify events that might trigger widget re-render
   */
  function setupEventListeners() {
    // Variant change events
    document.addEventListener('variant:change', overrideAllWidgets);

    // Shopify section re-render
    document.addEventListener('shopify:section:load', overrideAllWidgets);

    // Bundle switcher tab changes (custom event from bundle-switcher.js)
    document.addEventListener('bundle-switcher:tab-changed', () => {
      // Delay to allow Appstle to re-initialize on the new panel
      setTimeout(overrideAllWidgets, 300);
      setTimeout(overrideAllWidgets, 800);
    });

    // PubSub events if available
    if (window.PUB_SUB_EVENTS && typeof subscribe === 'function') {
      subscribe(window.PUB_SUB_EVENTS.optionValueSelectionChange, () => {
        setTimeout(overrideAllWidgets, 200);
      });
    }
  }

  /**
   * Initialize
   */
  function init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setupObserver();
        setupEventListeners();
      });
    } else {
      setupObserver();
      setupEventListeners();
    }
  }

  init();
})();
